/**
 * One-time launch-day slate: drop cached listings from before the fixed
 * launch cutoff below. This cutoff is a hardcoded constant shared by every
 * visitor (not a per-browser first-visit timestamp), so anything created
 * on-chain or in the sim stores after this moment always lists for everyone,
 * regardless of when a given browser first loads Penguin Pad.
 */

export const LIVE_RESET_VERSION = 'live-launch-10'
const RESET_FLAG_KEY = 'penguin-pad-live-reset-version'

/**
 * Fixed launch-day cutoff shared by every visitor. Anything created at or
 * after this moment (on-chain timestamp or sim createdAt) is always listed.
 * This must stay set to a moment AFTER all pre-launch test/demo listings were
 * created, otherwise those old items will still pass the "is it new" check
 * and keep showing up. Update this constant only if a new, intentional
 * "clean slate" is needed (and bump LIVE_RESET_VERSION alongside it).
 *
 * live-launch-10: cutoff bumped again to a fresh "now" so every prior curve
 * and presale (including anything created during testing after the
 * live-launch-9 cutoff) is permanently hidden, and the platform starts with
 * a clean slate. Anything created from this moment forward always lists
 * for everyone.
 */
const LIVE_LAUNCH_CUTOFF_ISO = '2026-09-29T16:55:00Z'
export const LIVE_LAUNCH_CUTOFF_MS = new Date(LIVE_LAUNCH_CUTOFF_ISO).getTime()

const LISTING_KEYS = [
  'penguin-pad-sim-launches',
  'penguin-pad-sim-eth',
  'penguin-pad-sim-eth-by-wallet',
  'penguin-pad-sim-holdings',
  'penguin-pad-sim-holdings-by-wallet',
  'penguin-pad-sim-presales-v2',
  'penguin-pad-colony-activity',
  'penguin-pad-token-balances',
  'penguin-pad-colony-activity-by-wallet',
  'penguin-pad-token-balances-by-wallet',
  'penguin-pad-watchlist',
  'penguin-pad-watchlist-by-wallet',
  'penguin-pad-create-locks',
  'penguin-pad-live-genesis-v1',
  'penguin-pad-live-reset-at',
] as const

const REFRESH_EVENTS = [
  'penguin-pad-sim-launchpad',
  'penguin-pad-sim-presale',
  'penguin-pad-colony-change',
  'penguin-pad-watchlist-change',
] as const

export type ListingKind = 'curve' | 'presale'

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

/**
 * True when the given creation timestamp is at or after the shared launch
 * cutoff. This is the same for every visitor since the cutoff is a fixed
 * constant, not derived from local browser state.
 */
export function isAfterLiveReset(createdAtMs: number): boolean {
  return Number.isFinite(createdAtMs) && createdAtMs >= LIVE_LAUNCH_CUTOFF_MS
}

export function shouldListOnChainItem(createdAtMs: number, alreadyListed = false): boolean {
  if (alreadyListed && isAfterLiveReset(createdAtMs))
    return true
  return isAfterLiveReset(createdAtMs)
}

/**
 * One-time per-browser cleanup of legacy cached listings/local state from
 * before the shared cutoff model existed. Listing visibility itself no
 * longer depends on this having run - isAfterLiveReset() always compares
 * against the fixed cutoff regardless of local storage state.
 */
export function applyLiveListingReset() {
  if (!canUseStorage())
    return
  if (window.localStorage.getItem(RESET_FLAG_KEY) === LIVE_RESET_VERSION)
    return

  for (const key of LISTING_KEYS)
    window.localStorage.removeItem(key)

  window.localStorage.setItem(RESET_FLAG_KEY, LIVE_RESET_VERSION)

  for (const eventName of REFRESH_EVENTS)
    window.dispatchEvent(new Event(eventName))
}
